/**
 * Utilities for formatting objects as Pydantic class definitions
 */

import { DEFAULT_ALLOWED_MODELS } from '../config/modelConfig';
import { mapBackendToPythonType, getPythonArrayItemType } from './typeMappers';

export interface PydanticField {
  name: string;
  display_name: string;
  field_type: string;
  is_required: boolean;
  is_array?: boolean;
  array_item_type?: string;
  description?: string;
  allowed_values?: string[];
  reference_object?: PydanticObject; // For nested reference objects
  reference_object_id?: string; // The ID of the referenced object
  reference_object_name?: string; // The name of the referenced object
  fields?: PydanticField[]; // For JSON fields with nested structure
}

export interface PydanticObject {
  name: string;
  display_name: string;
  description?: string;
  fields: PydanticField[];
}

export function formatAsPydanticClass(obj: PydanticObject, indent: number = 0, processedClasses?: Set<string>): string {
  // Initialize processed classes set to prevent infinite recursion
  const processedClassesSet = processedClasses || new Set<string>();
  
  const indentStr = ' '.repeat(indent);
  const className = obj.name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
  
  // If we've already processed this class, just return empty to avoid duplication
  if (processedClassesSet.has(className)) {
    return '';
  }
  processedClassesSet.add(className);
  
  let code = `${indentStr}class ${className}(BaseModel):\n`;
  code += `${indentStr}    """${obj.description || obj.display_name}"""\n`;
  
  if (!obj.fields || obj.fields.length === 0) {
    code += `${indentStr}    pass  # No fields defined\n`;
    return code;
  }
  
  // Recursively collect all nested classes
  const nestedClasses: string[] = [];
  const referencedTypes = new Map<string, string>();
  
  const collectNestedClasses = (fields: PydanticField[]) => {
    fields.forEach(field => {
      // Handle JSON fields with nested structure (check both 'fields' and 'nested_fields')
      const nestedFields = field.fields || (field as any).nested_fields;
      if (field.field_type === 'json' && nestedFields && nestedFields.length > 0) {
        const nestedClassName = field.name
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join('');
        
        if (!processedClassesSet.has(nestedClassName)) {
          const nestedClass = formatAsPydanticClass({
            name: field.name,
            display_name: field.display_name,
            description: field.description,
            fields: nestedFields
          }, 0, processedClassesSet);
          if (nestedClass) {
            nestedClasses.push(nestedClass);
          }
        }
        referencedTypes.set(field.name, nestedClassName);
      }
      
      // Handle reference fields
      if (field.field_type === 'reference' && field.reference_object) {
        const nestedClassName = field.reference_object.name
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join('');
        
        if (!processedClassesSet.has(nestedClassName)) {
          const nestedClass = formatAsPydanticClass(field.reference_object, 0, processedClassesSet);
          if (nestedClass) {
            nestedClasses.push(nestedClass);
          }
        }
        referencedTypes.set(field.name, nestedClassName);
      }
      
      // Also check array item types for references
      if (field.field_type === 'array' && field.array_item_type === 'reference') {
        if (field.reference_object) {
          const nestedClassName = field.reference_object.name
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
          
          if (!processedClassesSet.has(nestedClassName)) {
            const nestedClass = formatAsPydanticClass(field.reference_object, 0, processedClassesSet);
            if (nestedClass) {
              nestedClasses.push(nestedClass);
            }
          }
          referencedTypes.set(field.name, nestedClassName);
        } else if (field.reference_object_name) {
          // Even without the full object, store the class name for type annotation
          const nestedClassName = field.reference_object_name
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
          referencedTypes.set(field.name, nestedClassName);
        }
      }
    });
  };
  
  collectNestedClasses(obj.fields);
  
  // If we have nested classes, add them before the main class
  let fullCode = '';
  if (nestedClasses.length > 0 && indent === 0) {
    fullCode = nestedClasses.join('\n\n') + '\n\n';
  }
  
  fullCode += code;
  
  obj.fields.forEach(field => {
    let fieldType = mapBackendToPythonType(field.field_type);
    
    // Handle special cases
    if (field.field_type === 'json' && referencedTypes.has(field.name)) {
      // Use nested class name instead of 'dict'
      fieldType = referencedTypes.get(field.name)!;
    } else if (field.field_type === 'array') { 
      // Handle arrays
      if (field.array_item_type === 'reference') {
        // Reference array - check for nested class
        const refType = referencedTypes.get(field.name);
        if (refType) {
          fieldType = `List[${refType}]`;
        } else if (field.reference_object_name) {
          // Generate class name from reference_object_name
          const refClassName = field.reference_object_name
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
          fieldType = `List[${refClassName}]`;
        } else {
          fieldType = 'List[dict]';
        }
      } else {
        // Simple array with basic types
        const itemType = field.array_item_type ? getPythonArrayItemType(field.array_item_type) : 'str';
        fieldType = `List[${itemType}]`;
      }
    } else if (field.field_type === 'reference') {
      // Handle reference fields
      if (referencedTypes.has(field.name)) {
        fieldType = referencedTypes.get(field.name)!;
      } else if (field.reference_object_name) {
        // Generate class name from reference_object_name
        fieldType = field.reference_object_name
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join('');
      }
    }
    
    if (field.is_array && field.field_type !== 'array') {
      fieldType = `List[${fieldType}]`;
    }
    
    if (!field.is_required) {
      fieldType = `Optional[${fieldType}]`;
    }
    
    const defaultValue = field.is_required ? '...' : 'None';
    const constraints: string[] = [];
    
    if (field.description) {
      constraints.push(`description="${field.description}"`);
    }
    
    if (field.allowed_values && field.allowed_values.length > 0) {
      const enumValues = field.allowed_values.map(v => `"${v}"`).join(', ');
      constraints.push(`examples=[${enumValues}]`);
    }
    
    const fieldDef = constraints.length > 0 
      ? `Field(${defaultValue}, ${constraints.join(', ')})` 
      : defaultValue;
    
    fullCode += `${indentStr}    ${field.name}: ${fieldType} = ${fieldDef}\n`;
  });
  
  return fullCode;
}

export function formatFunctionAsPydantic(functionData: any): string {
  let code = `# AI Function: ${functionData.display_name || functionData.name}\n`;
  code += `# Description: ${functionData.description || 'No description'}\n\n`;
  
  code += `function_config = {\n`;
  code += `    "name": "${functionData.name}",\n`;
  code += `    "display_name": "${functionData.display_name || functionData.name}",\n`;
  code += `    "description": "${functionData.description || ''}",\n`;
  code += `    "input_types": ${JSON.stringify(functionData.input_types || ['text'])},\n`;
  code += `    "allowed_models": ${JSON.stringify(functionData.allowed_models || DEFAULT_ALLOWED_MODELS)},\n`;
  code += `    "response_type": "${functionData.response_type || 'text'}"\n`;
  code += `}\n\n`;
  
  code += `# Instructions:\n`;
  code += `"""\n${functionData.instructions || 'No instructions provided'}\n"""\n`;
  
  return code;
}
